import { useState, useCallback } from 'react';
import { X, Send, Loader2, ClipboardList } from 'lucide-react';
import type { QuizConfig, QuizQuestion, QuizAnswerSubmission } from '@/types';
import { quizService } from '@/services/quizService';
import MCQQuestion from './MCQQuestion';
import ShortAnswerQuestion from './ShortAnswerQuestion';
import CalculationQuestion from './CalculationQuestion';
import QuizResults from './QuizResults';

interface Props {
  quiz: QuizConfig;
  courseId: string;
  experimentNumber: number;
  labData?: Record<string, unknown>;
  onClose: () => void;
}

export default function QuizPanel({ quiz, courseId, experimentNumber, labData, onClose }: Props) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [score, setScore] = useState<{ total: number; correct: number; percentage: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnswer = useCallback((questionId: string, answer: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  }, []);

  const gradeLocally = (question: QuizQuestion, answer: string): { isCorrect: boolean | null; score: number | null; feedback: string | null } => {
    if (question.type === 'mcq') {
      const correct = answer === question.correctOptionId;
      return { isCorrect: correct, score: correct ? 1 : 0, feedback: question.explanation || null };
    }
    if (question.type === 'calculation') {
      const numAnswer = parseFloat(answer);
      if (isNaN(numAnswer)) return { isCorrect: false, score: 0, feedback: 'Invalid number' };
      const expected = question.expectedValue;
      const diff = expected === 0 ? Math.abs(numAnswer) : Math.abs((numAnswer - expected) / expected) * 100;
      const correct = diff <= question.tolerance;
      return { isCorrect: correct, score: correct ? 1 : 0, feedback: `Expected: ${expected} ${question.unit} (±${question.tolerance}%)` };
    }
    // Short answer - not auto-graded
    return { isCorrect: null, score: null, feedback: null };
  };

  const handleSubmit = async () => {
    // Check all questions are answered
    const unanswered = quiz.questions.filter(q => !answers[q.id] || answers[q.id].trim() === '');
    if (unanswered.length > 0) {
      setError(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }

    setSubmitting(true);
    setError(null);

    // Grade locally for MCQ and calculation questions
    const submissions: QuizAnswerSubmission[] = quiz.questions.map(q => {
      const answer = answers[q.id];
      const grade = gradeLocally(q, answer);
      return {
        questionId: q.id,
        questionType: q.type,
        answer,
        ...grade,
      };
    });

    // Calculate score from auto-gradeable questions
    const gradeable = submissions.filter(s => s.isCorrect !== null);
    const correct = gradeable.filter(s => s.isCorrect === true).length;
    const total = quiz.questions.length;
    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;

    setScore({ total, correct, percentage });
    setShowResults(true);

    // Try to submit to backend (non-blocking, works even without auth)
    try {
      await quizService.submitAll(courseId, experimentNumber, submissions);
    } catch {
      // Backend submission is optional - quiz works offline
      console.warn('Quiz backend submission failed - results saved locally');
    }

    setSubmitting(false);
  };

  const handleRetry = () => {
    setAnswers({});
    setShowResults(false);
    setScore(null);
    setError(null);
  };

  const answeredCount = quiz.questions.filter(q => answers[q.id] && answers[q.id].trim() !== '').length;

  return (
    <div className="quiz-panel-overlay">
      <div className="quiz-panel">
        {/* Header */}
        <div className="quiz-header">
          <div className="quiz-header-left">
            <ClipboardList size={20} />
            <h3>{quiz.title}</h3>
          </div>
          <button className="quiz-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Questions (and results if submitted) */}
        <div className="quiz-questions">
          {/* Score results - inside scrollable area */}
          {showResults && score && !submitting ? (
            <QuizResults score={score} onRetry={handleRetry} onClose={() => setShowResults(false)} />
          ) : null}

          {quiz.questions.map((q, idx) => (
            <div key={q.id} className="quiz-question-wrapper">
              <div className="question-number">Q{idx + 1}</div>
              {q.type === 'mcq' && (
                <MCQQuestion
                  question={q}
                  selectedAnswer={answers[q.id] || null}
                  onAnswer={handleAnswer}
                  showResults={showResults}
                />
              )}
              {q.type === 'short_answer' && (
                <ShortAnswerQuestion
                  question={q}
                  answer={answers[q.id] || ''}
                  onAnswer={handleAnswer}
                  showResults={showResults}
                />
              )}
              {q.type === 'calculation' && (
                <CalculationQuestion
                  question={q}
                  answer={answers[q.id] || ''}
                  onAnswer={handleAnswer}
                  showResults={showResults}
                  labData={labData}
                />
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        {!showResults && (
          <div className="quiz-footer">
            {error && <div className="quiz-error">{error}</div>}
            <div className="quiz-footer-row">
              <span className="quiz-progress">{answeredCount}/{quiz.questions.length} answered</span>
              <button
                className="quiz-submit-btn"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
                {submitting ? 'Submitting...' : 'Submit Quiz'}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{quizStyles}</style>
    </div>
  );
}

const quizStyles = `
  .quiz-panel-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }

  .quiz-panel {
    background: var(--bg-surface, #2C2C2C);
    border-radius: 16px;
    border: 1px solid var(--border, #4A4A4A);
    width: 100%;
    max-width: 700px;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .quiz-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border, #4A4A4A);
    background: var(--bg-elevated, #333);
  }

  .quiz-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--primary, #FF5A36);
  }

  .quiz-header h3 {
    margin: 0;
    font-size: 16px;
    color: var(--text-primary, #fff);
  }

  .quiz-close-btn {
    background: transparent;
    border: none;
    color: var(--text-secondary, #ccc);
    cursor: pointer;
    padding: 6px;
    border-radius: 6px;
    display: flex;
    transition: all 0.2s;
  }

  .quiz-close-btn:hover {
    background: rgba(255, 255, 255, 0.1);
    color: var(--text-primary);
  }

  .quiz-questions {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .quiz-question-wrapper {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .question-number {
    background: var(--primary, #FF5A36);
    color: white;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .quiz-question {
    flex: 1;
  }

  .question-text {
    font-size: 14px;
    color: var(--text-primary, #fff);
    margin: 0 0 12px 0;
    line-height: 1.5;
  }

  /* MCQ styles */
  .mcq-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .mcq-option {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: var(--bg-elevated, #333);
    border: 2px solid var(--border, #4A4A4A);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;
    color: var(--text-primary, #fff);
    font-size: 13px;
  }

  .mcq-option:hover:not(:disabled) {
    border-color: var(--primary, #FF5A36);
    background: rgba(255, 90, 54, 0.05);
  }

  .mcq-option.selected {
    border-color: var(--primary, #FF5A36);
    background: rgba(255, 90, 54, 0.1);
  }

  .mcq-option.correct {
    border-color: #00A86B;
    background: rgba(0, 168, 107, 0.1);
  }

  .mcq-option.incorrect {
    border-color: #EF4444;
    background: rgba(239, 68, 68, 0.1);
  }

  .option-indicator {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--bg-dark, #1A1A1A);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
  }

  .mcq-option.correct .option-indicator {
    background: #00A86B;
    color: white;
  }

  .mcq-option.incorrect .option-indicator {
    background: #EF4444;
    color: white;
  }

  .mcq-option.selected .option-indicator {
    background: var(--primary, #FF5A36);
    color: white;
  }

  /* Short answer styles */
  .short-answer-input {
    width: 100%;
    padding: 12px 16px;
    background: var(--bg-elevated, #333);
    border: 2px solid var(--border, #4A4A4A);
    border-radius: 10px;
    color: var(--text-primary, #fff);
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    transition: border-color 0.2s;
  }

  .short-answer-input:focus {
    outline: none;
    border-color: var(--primary, #FF5A36);
  }

  .short-answer-input:disabled {
    opacity: 0.7;
  }

  /* Calculation styles */
  .calc-input-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .calc-input {
    padding: 12px 16px;
    background: var(--bg-elevated, #333);
    border: 2px solid var(--border, #4A4A4A);
    border-radius: 10px;
    color: var(--text-primary, #fff);
    font-size: 14px;
    width: 200px;
    transition: border-color 0.2s;
  }

  .calc-input:focus {
    outline: none;
    border-color: var(--primary, #FF5A36);
  }

  .calc-input.correct { border-color: #00A86B; }
  .calc-input.incorrect { border-color: #EF4444; }
  .calc-input:disabled { opacity: 0.7; }

  .calc-unit {
    color: var(--text-secondary, #ccc);
    font-size: 14px;
  }

  .calc-result-icon {
    font-size: 20px;
    font-weight: 700;
  }

  .calc-result-icon.correct { color: #00A86B; }
  .calc-result-icon.incorrect { color: #EF4444; }

  .auto-fill-note {
    font-size: 11px;
    color: var(--secondary, #00A86B);
    margin-top: 6px;
    font-style: italic;
  }

  /* Explanation */
  .question-explanation {
    margin-top: 10px;
    padding: 10px 14px;
    background: rgba(59, 130, 246, 0.1);
    border-radius: 8px;
    border-left: 3px solid #3B82F6;
    font-size: 12px;
    color: var(--text-secondary, #ccc);
    line-height: 1.5;
  }

  .sample-answer {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(59, 130, 246, 0.2);
  }

  /* Footer */
  .quiz-footer {
    padding: 16px 20px;
    border-top: 1px solid var(--border, #4A4A4A);
    background: var(--bg-elevated, #333);
  }

  .quiz-error {
    color: #EF4444;
    font-size: 13px;
    margin-bottom: 10px;
  }

  .quiz-footer-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .quiz-progress {
    font-size: 13px;
    color: var(--text-secondary, #ccc);
  }

  .quiz-submit-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--secondary, #00A86B);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .quiz-submit-btn:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  .quiz-submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .quiz-submit-btn .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
